import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {HeaderService} from '../../core/services/header.service';
import {Router} from '@angular/router';
import {AuthService} from '../../core/security/auth.service';
import {merge, Subscription} from 'rxjs';
import {RolesEnum} from '../../core/security/roles.enum';
import {MatPaginator} from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {tap} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {FaxService as FaxApiService} from '../../api/services/fax.service';
import {FaxDatasource} from './fax.datasource';
import {FaxesRequest} from '../../api/models/faxes-request';
import {LocalFaxService} from '../local-fax.service';
import {MatConfirmDialogComponent} from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import {FaxModel} from '../../api/models/fax-model';
import {NotificationService} from '../../notification/notification.service';
import {FaxPreviewDialogComponent} from '../fax-preview/fax-preview-dialog.component';
import {EventsService} from '../../events/events.service';
import {FaxRetryDialogComponent} from '../fax-retry-dialog/fax-retry-dialog.component';
import {FaxNotesEditDialogComponent} from './fax-notes-edit-dialog.component';
import { FlexModule } from '@angular/flex-layout';
import { NgIf, NgClass, AsyncPipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatIconButton, MatIconAnchor } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';
import { DateTimePipe } from '../../core/pipes/date-time.pipe';

@Component({
    selector: 'app-fax-grid',
    templateUrl: './fax-grid.component.html',
    styleUrls: ['./fax-grid.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        NgIf,
        MatProgressSpinner,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatCellDef,
        MatCell,
        NgClass,
        MatIconButton,
        MatTooltip,
        MatIcon,
        MatIconAnchor,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        AsyncPipe,
        PhoneNumberPipe,
        DateTimePipe,
    ],
})
export class FaxGridComponent implements OnDestroy, OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'faxDate',
    'type',
    'tnTo',
    'tnFrom',
    'createdBy',
    'status',
    'pages',
    'internalNotes',
    'actions',
  ];
  @Input() filterChangeEvent: EventEmitter<any>;

  @Input() hasContactsModule = false;
  @Input() hasContactsEditRight = false;

  @Input() userRights = {
    previewFax: false,
    downloadFax: false,
    deleteFax: false,
  };

  initialLoad = true;

  hasSupportAccess = false;
  additionalFilter: any = {};
  recIdFilter: string;
  searchText: string;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource: FaxDatasource;
  detailsUrl: string;
  hasEditAccess: boolean;
  private faxInSubscription: Subscription;
  private faxOutSubscription: Subscription;
  private eventConnectSubscription: Subscription;


  constructor(public dialog: MatDialog,
              public headerService: HeaderService, public authService: AuthService,
              public faxService: LocalFaxService,
              private faxApiService: FaxApiService,
              private notificationService: NotificationService,
              private router: Router,
              private eventService: EventsService) {
  }

  ngOnInit() {
    this.hasSupportAccess = this.authService.hasAccess([RolesEnum.Support]);
    this.hasEditAccess = this.authService.hasAccess([RolesEnum.Fax_Edit, RolesEnum.Fax_Delete]);
    this.dataSource = new FaxDatasource(this.faxApiService);
    this.detailsUrl = '../user';


    this.faxInSubscription = this.eventService.faxInEmitter.subscribe(async (data) => {
      console.log('refresh fax-in', data);
      await this.loadData();
    });

    this.faxOutSubscription = this.eventService.faxOutEmitter.subscribe(async (data) => {
      console.log('refresh fax-out', data);
      await this.loadData();
    });

    this.eventConnectSubscription = this.eventService.connectEmitter.subscribe(async () => {
      if (!this.initialLoad) {
        await this.loadData();
      }
    });
  }

  ngAfterViewInit() {
    this.filterChangeEvent?.subscribe((filter) => {
      this.additionalFilter = filter;
      this.paginator.pageIndex = 0;
    });

    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

    merge(this.sort.sortChange, this.paginator.page, this.filterChangeEvent)
      .pipe(
        tap(() => this.loadData())
      )
      .subscribe();

    this.initialLoad = false
  }

  loadData() {
    console.log('filter changed');
    this.dataSource.queryData(this.getDataFilter());
  }

  getDataFilter(): FaxesRequest {
    const baseRequest = {
      skip: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize,
      sort: [{field: this.sort.active, direction: this.sort.direction ? this.sort.direction.toUpperCase() : undefined}],
    };

    return Object.assign(baseRequest, this.additionalFilter || {});
  }

  ngOnDestroy() {
    this.faxInSubscription?.unsubscribe();
    this.faxOutSubscription?.unsubscribe();
    this.eventConnectSubscription?.unsubscribe();
  }

  addNewContact($event: MouseEvent, faxNumber) {
    this.router.navigate([this.headerService.themePrefix, 'contacts', 'contact', 'new', 'edit'], {queryParams: {faxNumber: faxNumber}});
  }

  previewPdf($event: MouseEvent, fax: FaxModel) {
    this.dialog.open(FaxPreviewDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        pdfSource: `/api/fax/preview-fax/${fax.callerId}/${fax.faxId}`
      }
    });
  }

  onOpenRetryDialog($event: MouseEvent, fax: FaxModel) {
    const dialogRef = this.dialog.open(FaxRetryDialogComponent, {
      width: '500px',
      height: '90%',
      data: {
        faxData: fax
      }
    });

    dialogRef.afterClosed().subscribe((value) => {
      if (value) {
        this.loadData();
      }
    });
  }

  onDeleteFaxConfirm($event: MouseEvent, fax: FaxModel) {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      // height: '90%',
      data: {
        title: 'Delete Fax Confirmation',
        actionTitle: 'Delete',
        text: 'Are you sure you want to delete the selected fax?',
        hideCancel: false
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.faxService.deleteFax(fax.callerId, fax.faxId);
        await this.notificationService.notifySuccess('Fax deleted successfully');
        this.loadData();
      }
    });
  }

  async markRowAsUnread(fax: FaxModel) {
    console.log('fax clicked', fax);
    if (fax && fax.unread) {
      if (fax.type === 'incoming') {
        await this.faxService.markNotificationAsRead(`${fax.callerId}-${fax.tnFrom}-${fax.faxId}`);
      } else {
        await this.faxService.markNotificationAsRead(`${fax.callerId}-${fax.tnTo}-${fax.faxId}`);
      }
      this.filterChangeEvent.emit(this.additionalFilter);
    }
  }

  async onMarkAsUnread($event: MouseEvent, fax: FaxModel) {
    await this.faxService.markNotificationAsUnRead(`${fax.callerId}-${fax.tnFrom}-${fax.faxId}`);
    this.filterChangeEvent.emit(this.additionalFilter);
  }

  editInternalNotes(row: FaxModel) {
    const dialogRef = this.dialog.open(FaxNotesEditDialogComponent, {
      width: '400px',
      data: {
        faxId: row.faxId,
        faxInternalNotes: row.internalNotes,
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.loadData();
      }
    });
  }
}
